import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomTable from "../../components/CustomTable";
import { managerService } from "../../../../services/api/managerService";
import { AuthContext } from "../../../../contexts/AuthContext";
import { toast } from "react-toastify";
import Loading from "../../components/Loading";
import MobilePatternHeader from "../../components/MobilePatternHeader";
import { formatDate } from "../../../../utils/DateFormat";
import { StatusBadge } from "../../../../constants/statusConstants";
import Button from "../../components/Button";

const StaffList = ({ isMobile }) => {
  const { token_client } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // list state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  // paging
  const currentPage = Number(searchParams.get("page")) || 1;
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const abortRef = useRef(null);

  const addNew = () => navigate("/tai-khoan-nhan-vien/tao-nhan-vien-moi");

  const normalizeEntry = useCallback(
    (entry, page, index) => {
      const account = entry?.account || {};
      const id = itemsPerPage * (page - 1) + index + 1;
      return {
        nav_id: entry?._id,
        id,
        avatar: account?.avatar || "/images/avatar-default.png",
        manv: entry?.staffCode || "--",
        hoten:
          account?.fullName ||
          account?.companyName ||
          account?.username ||
          "--",
        mail: account?.email || "--",
        phone: entry?.phone || account?.phone || "--",
        joinDate: entry?.joinDate ? formatDate(entry.joinDate) : "--",
        trangThai: entry?.status || "--",
      };
    },
    [itemsPerPage]
  );

  const formatStaffData = useCallback(
    (data, page) => {
      const employees = (data?.employees || []).map((e, i) =>
        normalizeEntry(e, page, i)
      );
      const baseIndex = employees.length;
      const collaborators = (data?.collaborators || []).map((c, i) =>
        normalizeEntry(c, page, baseIndex + i)
      );
      return [...employees, ...collaborators];
    },
    [normalizeEntry]
  );

  const fetchData = useCallback(
    async ({ page = 1, limit = itemsPerPage, append = false } = {}) => {
      abortRef.current?.abort?.();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        if (!append) {
          setLoading(true);
          setError("");
        }

        const res = await managerService.get_all_staff(
          page,
          limit,
          "",
          token_client,
          controller.signal
        );

        const formatted = formatStaffData(res?.data, page);
        setRows((prev) => (append ? [...prev, ...formatted] : formatted));

        const apiTotalPages =
          res?.data?.totalPages ||
          res?.pagination?.totalPages ||
          res?.meta?.totalPages ||
          1;

        setTotalPages(Number(apiTotalPages) || 1);

        setSearchParams((prev) => {
          const p = new URLSearchParams(prev);
          p.set("page", String(page));
          return p;
        });
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.error("get_all_staff error:", err);
        const msg =
          err?.response?.data?.message || "Không thể kết nối tới server.";
        setError(msg);
        toast.dismiss();
        toast.error(msg);
        if (!append) setRows([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [token_client, itemsPerPage, formatStaffData, setSearchParams]
  );

  useEffect(() => {
    fetchData({ page: currentPage, limit: itemsPerPage, append: false });
    return () => abortRef.current?.abort?.();
  }, [currentPage, itemsPerPage, fetchData]);

  // Infinite scroll cho mobile
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!isMobile) return;
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (
          e.isIntersecting &&
          !loading &&
          !loadingMore &&
          currentPage < totalPages
        ) {
          const nextPage = currentPage + 1;
          setLoadingMore(true);
          fetchData({ page: nextPage, limit: itemsPerPage, append: true }).then(
            () => {
              setSearchParams((prev) => {
                const p = new URLSearchParams(prev);
                p.set("page", String(nextPage));
                return p;
              });
            }
          );
        }
      },
      { rootMargin: "120px" }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [
    isMobile,
    loading,
    loadingMore,
    currentPage,
    totalPages,
    fetchData,
    itemsPerPage,
    setSearchParams,
  ]);

  const columns = useMemo(
    () => [
      {
        key: "id",
        title: "STT",
        minWidth: "20px",
        align: "text-left",
        render: (value) => <div className="pl-2">{value}</div>,
      },
      {
        key: "manv",
        title: "Mã nhân viên",
        minWidth: "150px",
        color: "text-[#0954E9]",
      },
      { key: "hoten", title: "Họ tên", minWidth: "200px" },
      { key: "mail", title: "Email", minWidth: "200px" },
      { key: "phone", title: "Số điện thoại", minWidth: "150px" },
      { key: "joinDate", title: "Ngày vào làm", minWidth: "200px" },
      {
        key: "trangThai",
        title: "Trạng thái",
        minWidth: "150px",
        render: (value) => (
          <StatusBadge status={value} className="min-w-[120px]" />
        ),
      },
    ],
    []
  );

  /* ========== Loading lần đầu ========== */
  if (loading && !loadingMore && rows.length === 0) {
    return (
      <div
        className={`flex items-center justify-center ${isMobile ? "h-screen w-screen" : "h-full"
          }`}
      >
        <Loading />
      </div>
    );
  }

  /* ========== RENDER ========== */
  return isMobile ? (
    <div className="h-full w-full">
      <MobilePatternHeader
        title="Quản lý tài khoản"
        onBack={() => navigate(-1)}
      />

      <div className="h-[calc(100%-64px)] bg-[#F2F2F2]">
        <div className="h-[calc(100%-140px)] overflow-auto">
          <div className="space-y-4 p-3">
            {rows.length > 0 ? (
              rows.map((acc) => (
                <div
                  key={acc.nav_id}
                  className="w-full bg-white rounded-lg p-3 border border-[#EAEAEA] hover:bg-[#f9f9f9]"
                  onClick={() =>
                    navigate(`/tai-khoan-nhan-vien/nhan-vien/${acc.nav_id}`)
                  }
                >
                  <div className="flex justify-between items-center text-[14px]">
                    <div className="flex gap-2 items-center min-w-0">
                      <img
                        src={acc.avatar}
                        alt="avatar"
                        className="rounded-lg size-[40px] object-cover"
                      />
                      <div className="min-w-0">
                        <p className="font-sf-medium truncate">{acc.hoten}</p>
                        <p className="text-xs opacity-70 truncate">
                          {acc.mail}
                        </p>
                      </div>
                    </div>
                    <StatusBadge
                      status={acc.trangThai}
                      className="min-w-[96px]"
                    />
                  </div>
                </div>
              ))
            ) : error ? (
              <div className="text-center text-sm text-red-500 py-6">
                {error}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[50vh]">
                <img src="/images/empty.png" alt="Empty" />
              </div>
            )}

            {loadingMore && (
              <div className="text-center py-3 text-sm text-gray-500">
                Đang tải thêm...
              </div>
            )}

            <div ref={sentinelRef} className="h-1" />
          </div>
        </div>
      </div>

      <div className="w-full p-3 fixed bottom-[85px] left-0">
        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={addNew}
          className="w-full"
        >
          Tạo mới
        </Button>
      </div>
    </div>
  ) : (
    //  DESKTOP: fit khung, chỉ scroll vùng table
    <div className="h-full min-h-0 flex flex-col">
      {/* Top actions (không scroll) */}
      <div className="mb-4 flex shrink-0 justify-end">
        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={addNew}
          leftIcon={<img src="/icons/user-plus.svg" alt="" />}
        >
          Tạo mới
        </Button>
      </div>

      {/* Table area scroll */}
      <div className="flex-1 min-h-0 overflow-auto">
        <CustomTable
          data={rows}
          columns={columns}
          currentPage={currentPage}
          setCurrentPage={(page) =>
            setSearchParams((prev) => {
              const p = new URLSearchParams(prev);
              p.set("page", page.toString());
              return p;
            })
          }
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalPages={totalPages}
          setView
          setEdit
          basePath="/tai-khoan-nhan-vien/nhan-vien"
        />
      </div>
    </div>
  );
};

export default StaffList;
